import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Guaranty} from '../../../../../../domain/guaranty/model/guaranty';
import {WidgetDetail} from '../../../../../../domain/widget/model/widget-detail';
import {WidgetSize} from '../../../../../../domain/widget/model/widget-size.enum';
import {Chart} from 'angular-highcharts';
import * as Highcharts from 'highcharts';
import {GuarantyDistribution} from '../../../../../../domain/guaranty/model/guaranty-distribution';
import {DomainUrlService} from '../../../../../../application/config/domain-url.service';
import {GuarantyLegend} from '../../../../../../domain/guaranty/model/guaranty-legend';
import {TranslateService} from '@ngx-translate/core';
import {graphColors} from '../../../../../../domain/widget/widget.util';
import {ApplicationName} from '../../../../../../domain/core/model/aplication-name.enum';
import {UserContextService} from '../../../../../../application/app-context/user-context/user-context.service';

@Component({
             selector: 'app-widget-guaranty',
             templateUrl: './home-dashboard-widget-guaranty.component.html'
           })
export class HomeDashboardWidgetGuarantyComponent implements OnInit {

  @Input() guarantyData: Guaranty;
  @Input() widget: WidgetDetail;

  readonly WidgetSize = WidgetSize;

  chart: Chart;
  color: Array<string>;
  beneficiaryColorStartIndex: number;
  legend: Array<GuarantyLegend>;
  otherLabel = 'widget.guaranty.others';
  otherColor = '#DEE0EA';
  allianceAccountId: number;

  @Output() actionEvent = new EventEmitter<string>();

  constructor(private domainUrlService: DomainUrlService,
              private translateService: TranslateService,
              private userContextService: UserContextService) {
    const userContext = this.userContextService.getUserContext();
    const currentAccount = userContext.user.accounts.find(account => account.accountId === userContext.accountId);
    this.allianceAccountId = currentAccount.applications.find(application => application.targetApp
                                                                             === ApplicationName.ALLIANCE).accountSynchId;
  }

  ngOnInit(): void {
    this.color = graphColors;
    this.beneficiaryColorStartIndex = this.guarantyData.categories.length - 1;
    this.legend = this.legendBuilder();
    this.chart = this.loadChart();
  }

  public goToRiskCapPage(): string {
    return `${this.domainUrlService.getAllianceUrl()}?accountId=${this.allianceAccountId}&redirectLink=analyseRatioAlliance`;
  }

  private loadChart(): Chart {
    const spec = {
      credits: {
        enabled: false
      },
      chart: {
        type: 'pie',
        spacing: [1, 1, 1, 1],
        marginTop: 0,
        spacingTop: 0,
        spacingBottom: 0,
        marginBottom: 0,
        plotBorderColor: '#6a7480',
        backgroundColor: '#ffffff',
        plotBorderWidth: 0,
        style: {
          fontFamily: 'Arial, Helvetica, sans-serif',
          'font-family': 'Arial, Helvetica, sans-serif'
        }
      },
      tooltip: {
        headerFormat: '<small>{series.name}</small></br>',
        pointFormat: '{point.name}:  <b>{point.y}%</b>',
        outside: true
      },
      title: {
        text: null
      },
      subtitle: {
        text: null
      },
      exporting: {
        enabled: false
      },
      plotOptions: {
        series: {
          states: {
            hover: {
              halo: {
                size: 0
              }
            }
          }
        },
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            crop: false,
            distance: 8,
            connectorPadding: -3,
            style: {
              textOverflow: 'ellipsis'
            }
          }
        }
      },
      series: [
        {
          name: this.translateService.instant('widget.guaranty.category'),
          type: 'pie',
          size: '100%',
          innerSize: '90%',
          data: this.getCategoriesData()
        },
        {
          name: this.translateService.instant('widget.guaranty.beneficiary'),
          type: 'pie',
          size: '80%',
          data: this.getBeneficiariesData()
        }
      ],
      legend: {
        enabled: false
      }
    };

    return new Chart(spec as Highcharts.Options);
  }

  private getCategoriesData(): any[] {
    return this.guarantyData.categories.map((category: GuarantyDistribution, index: number) => {
      const name = category.name === this.otherLabel ? this.translateService.instant(this.otherLabel) : category.name;

      return {
        name,
        y: category.outstandingBalancePercent,
        color: this.getGraphColor(category.name, index)
      };
    });
  }

  private getBeneficiariesData(): any[] {
    return this.guarantyData.beneficiaries.map((beneficiary: GuarantyDistribution, index: number) => {
      const colorIndex = index + this.beneficiaryColorStartIndex;
      const name = beneficiary.name === this.otherLabel ? this.translateService.instant(this.otherLabel) : beneficiary.name;

      return {
        name,
        y: beneficiary.outstandingBalancePercent,
        color: this.getGraphColor(beneficiary.name, colorIndex),
        dataLabels: {enabled: false}
      };
    });
  }

  private legendBuilder(): Array<GuarantyLegend> {
    return this.guarantyData.beneficiaries.map((beneficiary, index) => {
      const colorIndex = index + this.beneficiaryColorStartIndex;
      const color = this.getGraphColor(beneficiary.name, colorIndex);
      const name = beneficiary.name === this.otherLabel ? this.translateService.instant(this.otherLabel) : beneficiary.name;

      return new GuarantyLegend(name, color);
    });
  }

  private getGraphColor(name: string, index: number): string {
    return name === this.otherLabel ? this.otherColor : this.color[index];
  }
}
